import { blobMediaUrl } from "@/lib/blob-url"
import type { Translator } from "@/lib/intl-types"
import type { BiomarkerLandingConfig } from "@/components/ui/biomarker-landing"

/**
 * Cortisol single-biomarker landing page content.
 *
 * Structure duplicated from the Lp(a) page; EN copy rewritten for cortisol and
 * checked against clinical research (see cortisol-research-notes.md). Copy is
 * deliberately conservative: a single morning blood cortisol is NOT a validated
 * marker of chronic stress, burnout or "adrenal fatigue", so the page describes
 * cortisol's rhythm and frames the reading as a doctor-interpreted starting
 * point, not a stress/burnout readout.
 *
 * Copy is sourced from the `go` i18n namespace (keys prefixed `cortisol*`).
 * `en.json` is the source of truth; the other locales are still EN/Lp(a) stubs
 * pending translation + review-cockpit sign-off (see /translation-workflow).
 * Some fields stay as literals: image alt text, the rating, units, the FAQ
 * fallback (the live FAQ comes from the CMS) + the unused personas + explorer +
 * self-check blocks (not rendered by the client).
 *
 * STILL NEEDS BEFORE LAUNCH: the partner-lab morning reference range (explorer
 * numbers are placeholders), real cortisol images, and clinical / HWG sign-off.
 */
export function cortisolConfig(
  t: Translator<"go">,
  whatWeDetectHref: string,
  advancedBloodTestHref: string,
  priceFrom?: string
): BiomarkerLandingConfig {
  return {
    slug: "cortisol-blood-test",
    advancedBloodTestHref,

    hero: {
      eyebrow: t("cortisolHeroEyebrow"),
      badge: t("cortisolHeroBadge"),
      ctaNote: t("cortisolHeroCtaNote"),
      ctaNoteHref: advancedBloodTestHref,
      heading: t("cortisolHeroHeading"),
      body: t("cortisolHeroBody"),
      primaryCta: t("cortisolHeroPrimaryCta"),
      secondaryCta: t("cortisolHeroSecondaryCta"),
      secondaryHref: "#what-it-is",
      // No `rating` here on purpose — an unsourced "5.0 · 34 reviews" literal
      // came across in the copy-paste from Lp(a). It is unrendered, but keeping
      // it is one wire-up away from a fabricated review count on an unlaunched
      // test, so it is dropped (matches the PSA page).
      priceFrom,
      image: {
        src: blobMediaUrl("/images/marketing/cortisol-hero-kitchen.jpeg"),
        alt: "Woman sitting at her kitchen table with a mug of coffee in the morning light",
        position: "center",
      },
      // Single morning result card, kept consistent with the product: one
      // morning draw, not a morning + evening pair. We deliberately do NOT show
      // an evening reading. It implied a second draw the customer never gets,
      // and a green "naturally lower" evening card wrongly suggested that lower
      // cortisol is better. Morning cortisol should be high; a healthy result is
      // a strong morning level, not a low one. Value is an illustrative literal
      // (NEEDS CLINICAL SIGN-OFF).
      floatingCards: [
        {
          name: t("cortisolHeroCardCholesterolName"),
          value: "480 nmol/L",
          status: t("cortisolHeroCardInRange"),
          tone: "good",
        },
      ],
    },

    // Test-detail info card (mirrors the /tests/<slug> quick-facts block).
    // NEEDS CLINICAL SIGN-OFF on the prep window and "for whom" claims.
    detailCard: {
      heading: t("cortisolDetailHeading"),
      benefits: [
        t("cortisolDetailBenefit1"),
        t("cortisolDetailBenefit2"),
        t("cortisolDetailBenefit3"),
        t("cortisolDetailBenefit4"),
      ],
      primaryCta: t("cortisolHeroPrimaryCta"),
      secondaryCta: {
        label: t("cortisolDetailSecondaryCta"),
        href: advancedBloodTestHref,
      },
      rows: [
        {
          icon: "healthAreas",
          label: t("cortisolDetailRow1Label"),
          value: t("cortisolDetailRow1Value"),
        },
        {
          icon: "time",
          label: t("cortisolDetailRow2Label"),
          value: t("cortisolDetailRow2Value"),
        },
        {
          icon: "radiation",
          label: t("cortisolDetailRow3Label"),
          value: t("cortisolDetailRow3Value"),
        },
        {
          icon: "contrast",
          label: t("cortisolDetailRow4Label"),
          value: t("cortisolDetailRow4Value"),
        },
        {
          icon: "preparation",
          label: t("cortisolDetailRow5Label"),
          value: t("cortisolDetailRow5Value"),
        },
        {
          icon: "forWhom",
          label: t("cortisolDetailRow6Label"),
          value: t("cortisolDetailRow6Value"),
        },
      ],
    },

    facts: {
      eyebrow: t("cortisolFactsEyebrow"),
      heading: t("cortisolFactsHeading"),
      headlineStat: {
        value: t("cortisolFactsStatValue"),
        caption: t("cortisolFactsStatCaption"),
      },
      // "Key benefits" (2) from the source content.
      items: [
        {
          title: t("cortisolFact1Title"),
          body: t("cortisolFact1Body"),
          icon: "heart",
        },
        {
          title: t("cortisolFact2Title"),
          body: t("cortisolFact2Body"),
          icon: "check",
        },
      ],
    },

    gap: {
      eyebrow: t("cortisolGapEyebrow"),
      heading: t("cortisolGapHeading"),
      body: t("cortisolGapBody"),
      image: {
        src: blobMediaUrl("/images/homepage/age-40s.jpg"),
        alt: "Healthy-looking person in their forties",
        position: "center",
      },
      // Auto-scrolling biomarker marquee (reused from /report). A standard panel
      // reads clean across the board while cortisol is never measured (greyed).
      // Values/units are illustrative literals; labels + statuses are i18n.
      marquee: [
        {
          label: t("cortisolMarker1"),
          value: "4.8",
          unit: "mmol/L",
          status: t("cortisolStatusNormal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 30, color: "#ed9062" },
            { h: 45, color: "#408c49", active: true },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 45,
          ghosts: [50, 42],
        },
        {
          label: t("cortisolMarker2"),
          value: "1.6",
          unit: "mmol/L",
          status: t("cortisolStatusOptimal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 50, color: "#408c49", active: true },
            { h: 25, color: "#c8b59d" },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 22,
          ghosts: [28, 25],
        },
        {
          label: t("cortisolMarker3"),
          value: "1.8",
          unit: "mmol/L",
          status: t("cortisolStatusNormal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 30, color: "#ed9062" },
            { h: 45, color: "#408c49", active: true },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 48,
          ghosts: [52, 46],
        },
        {
          label: t("cortisolMarker4"),
          value: "—",
          unit: "nmol/L",
          status: t("cortisolStatusNotMeasured"),
          statusBg: "",
          statusText: "",
          segments: [
            { h: 33, color: "#e5e5e5" },
            { h: 34, color: "#e5e5e5" },
            { h: 33, color: "#e5e5e5" },
          ],
          dotY: 50,
          ghosts: [],
          muted: true,
        },
      ],
    },

    explorer: {
      eyebrow: t("cortisolExplorerEyebrow"),
      heading: t("cortisolExplorerHeading"),
      caption: t("cortisolExplorerCaption"),
      unit: "nmol/L",
      // NOTE: this explorer section is NOT currently rendered by the client.
      // PLACEHOLDER morning-cortisol bands — unlike Lp(a), BOTH low and high are
      // the concern and within-range is normal, so the middle band is "good".
      // The 140 / 540 nmol/L cut-offs are the commonly-cited ~8 AM interval and
      // MUST be replaced with Ahead's partner-lab assay-specific reference range
      // before this section is ever shown. See cortisol-research-notes.md.
      sliderMax: 700,
      defaultValue: 330,
      bands: [
        {
          label: t("cortisolBand1Label"),
          tone: "warn",
          from: 0,
          to: 140,
          status: t("cortisolBand1Status"),
          level: 1,
          meaning: t("cortisolBand1Meaning"),
        },
        {
          label: t("cortisolBand2Label"),
          tone: "good",
          from: 140,
          to: 540,
          status: t("cortisolBand2Status"),
          level: 2,
          meaning: t("cortisolBand2Meaning"),
        },
        {
          label: t("cortisolBand3Label"),
          tone: "warn",
          from: 540,
          to: 700,
          status: t("cortisolBand3Status"),
          level: 3,
          meaning: t("cortisolBand3Meaning"),
        },
      ],
      factorsLabel: t("cortisolExplorerFactorsLabel"),
      factors: [
        t("cortisolFactor1"),
        t("cortisolFactor2"),
        t("cortisolFactor3"),
        t("cortisolFactor4"),
      ],
    },

    risks: {
      eyebrow: t("cortisolRisksEyebrow"),
      heading: t("cortisolRisksHeading"),
      body: t("cortisolRisksBody"),
      seeAllLabel: t("cortisolRisksSeeAll"),
      seeAllHref: whatWeDetectHref,
      // "Who might consider a cortisol test" — 4 cards.
      items: [
        {
          title: t("cortisolRisk1Title"),
          body: t("cortisolRisk1Body"),
          icon: "warning",
        },
        {
          title: t("cortisolRisk2Title"),
          body: t("cortisolRisk2Body"),
          icon: "moon",
        },
        {
          title: t("cortisolRisk3Title"),
          body: t("cortisolRisk3Body"),
          icon: "drop",
        },
        {
          title: t("cortisolRisk4Title"),
          body: t("cortisolRisk4Body"),
          icon: "stethoscope",
        },
      ],
    },

    selfCheck: {
      eyebrow: t("cortisolSelfCheckEyebrow"),
      heading: t("cortisolSelfCheckHeading"),
      intro: t("cortisolSelfCheckIntro"),
      questions: [
        { id: "family", label: t("cortisolSelfCheckQ1") },
        { id: "event", label: t("cortisolSelfCheckQ2") },
        { id: "cholesterol", label: t("cortisolSelfCheckQ3") },
        { id: "prevention", label: t("cortisolSelfCheckQ4") },
      ],
      results: [
        {
          minChecks: 0,
          tone: "good",
          title: t("cortisolSelfCheckResult1Title"),
          body: t("cortisolSelfCheckResult1Body"),
        },
        {
          minChecks: 1,
          tone: "warn",
          title: t("cortisolSelfCheckResult2Title"),
          body: t("cortisolSelfCheckResult2Body"),
        },
        {
          minChecks: 2,
          tone: "bad",
          title: t("cortisolSelfCheckResult3Title"),
          body: t("cortisolSelfCheckResult3Body"),
        },
      ],
      primaryCta: t("cortisolSelfCheckCta"),
    },

    action: {
      eyebrow: t("cortisolActionEyebrow"),
      heading: t("cortisolActionHeading"),
      body: t("cortisolActionBody"),
      items: [
        {
          title: t("cortisolAction1Title"),
          body: t("cortisolAction1Body"),
          icon: "actionPlan",
        },
        {
          title: t("cortisolAction2Title"),
          body: t("cortisolAction2Body"),
          icon: "stethoscope",
        },
        {
          title: t("cortisolAction3Title"),
          body: t("cortisolAction3Body"),
          icon: "chart",
        },
        {
          title: t("cortisolAction4Title"),
          body: t("cortisolAction4Body"),
          icon: "checklist",
        },
      ],
      image: {
        src: blobMediaUrl("/images/marketing/cortisol-action-forest-v2.jpeg"),
        alt: "Woman walking a sunlit woodland trail, smiling softly",
        position: "center",
      },
    },

    // Unused (the live TestimonialsSection replaces this block) — kept English.
    personas: {
      eyebrow: "Real-world reasons",
      heading: "People who decided to know their number",
      note: "Illustrative profiles, not testimonials.",
      items: [
        {
          name: "David, 45",
          scenario:
            "Persistent fatigue with no clear cause. He wanted his hormones, cortisol included, on record.",
          photo: {
            src: blobMediaUrl("/images/report/person-david.jpg"),
            alt: "Portrait of a man in his forties",
          },
        },
        {
          name: "Eva, 38",
          scenario:
            "She wanted a fuller picture of her hormones as part of a routine check-up.",
          photo: {
            src: blobMediaUrl("/images/report/person-eva.jpg"),
            alt: "Portrait of a woman in her late thirties",
          },
        },
        {
          name: "Lena, 48",
          scenario:
            "Disrupted sleep and low energy she wanted to look into properly with her doctor.",
          photo: {
            src: blobMediaUrl("/images/report/person-lena.jpg"),
            alt: "Portrait of a woman in her late forties",
          },
        },
      ],
    },

    biomarkerPanel: {
      eyebrow: t("cortisolPanelEyebrow"),
      heading: t("cortisolPanelHeading"),
      subheading: t("cortisolPanelSubheading"),
      ctaLabel: t("cortisolPanelCta"),
    },

    steps: {
      eyebrow: t("cortisolStepsEyebrow"),
      heading: t("cortisolStepsHeading"),
      body: t("cortisolStepsBody"),
      stepWord: t("cortisolStepsStepWord"),
      sampleReportCta: t("cortisolStepsSampleReport"),
      locationsCta: t("cortisolStepsLocationsCta"),
      items: [
        {
          title: t("cortisolStep1Title"),
          body: t("cortisolStep1Body"),
          image: {
            src: blobMediaUrl("/images/marketing/cortisol-step1-booked.jpeg"),
            alt: "Woman standing by a sunlit window at home, laptop open on the bed behind her",
          },
        },
        {
          title: t("cortisolStep2Title"),
          body: t("cortisolStep2Body"),
          image: {
            src: blobMediaUrl(
              "/images/marketing/cortisol-step2-blood-draw-v2.jpeg"
            ),
            alt: "Smiling patient during a blood draw with a clinician in a bright clinic",
          },
        },
        {
          title: t("cortisolStep3Title"),
          body: t("cortisolStep3Body"),
          image: {
            src: blobMediaUrl("/images/marketing/lpa-step3-report.jpeg"),
            alt: "Tablet showing a health report on a kitchen counter",
          },
        },
      ],
    },

    faq: {
      heading: t("cortisolFaqHeading"),
      // Fallback only — the live FAQ comes from the CMS (Advanced blood test set).
      // Placeholder cortisol copy, NEEDS CLINICAL SIGN-OFF before launch.
      items: [
        {
          q: "Why is cortisol measured in the morning?",
          a: "Cortisol follows a daily rhythm and peaks in the early morning. Measuring it then gives a reliable, comparable baseline. A random reading later in the day is much harder to interpret.",
        },
        {
          q: "Is cortisol on my normal blood test?",
          a: "Not usually. A standard panel focuses on markers like cholesterol and glucose. Cortisol needs to be requested specifically, which is why most people have never had it measured.",
        },
        {
          q: "Do I need to prepare for the test?",
          a: "The main thing is timing: your blood draw should be in the morning, ideally before 10 AM. Follow any fasting guidance that comes with your Advanced blood test.",
        },
        {
          q: "What does a high cortisol result mean?",
          a: "A single high morning reading has several possible causes and does not diagnose anything on its own. It is a prompt to look further, sometimes with follow-up tests. Your report explains your result and your doctor can help you decide what to do next.",
        },
        {
          q: "Is it covered by insurance?",
          a: "Coverage varies by insurer and plan. Your report and our team can help you understand what applies in your case.",
        },
      ],
    },

    closing: {
      eyebrow: t("cortisolClosingEyebrow"),
      heading: t("cortisolClosingHeading"),
      body: t("cortisolClosingBody"),
      primaryCta: t("cortisolClosingPrimaryCta"),
      secondaryCta: t("cortisolClosingSecondaryCta"),
      secondaryHref: "#what-it-is",
      image: {
        src: blobMediaUrl("/images/marketing/lpa-closing-hiker-trail.jpg"),
        alt: "Hiker on an alpine trail with snow-capped Swiss mountains",
        position: "center",
      },
    },
  }
}
